export const SITE_NAME = "Airon AS";
export const SITE_ORIGIN = "https://www.airon.no";
export const SITE_LOCALE = "nb_NO";

export function pagePath(page) {
  const slug = page?.slug;
  return slug ? `/side/${slug}` : "/";
}

export function postPath(post) {
  const slug = post?.slug;
  return slug ? `/innlegg/${slug}` : "/innlegg";
}

export function absoluteUrl(path) {
  if (!path) return SITE_ORIGIN;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_ORIGIN}${normalized}`;
}

export function unwrapCmsDocument(payload) {
  if (!payload) return null;
  const record = payload.web_page || payload;
  if (!record || (!record.id && !record.slug && !record.title)) return null;
  return {
    ...record,
    content: payload.content || record.content || { blocks: [] },
    live_page_url: payload.live_page_url || null,
  };
}

export function listCmsDocuments(payload) {
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  return rows.map((row) => unwrapCmsDocument(row)).filter(Boolean);
}

export function documentExcerpt(doc, fallback = "") {
  return (
    String(doc?.excerpt || doc?.meta_description || fallback || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

export function documentKeywords(doc) {
  const fromMeta = String(doc?.meta_keywords || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const fromTags = Array.isArray(doc?.tags)
    ? doc.tags.map((tag) => String(tag).trim()).filter(Boolean)
    : [];
  return [...new Set([...fromMeta, ...fromTags])];
}

export function robotsContent(doc) {
  return doc?.noindex ? "noindex, follow" : "index, follow";
}

export function cmsSeo(doc, { kind = "page", path } = {}) {
  const isPost = kind === "post" || doc?.kind === "post";
  const titleBase = doc?.meta_title || doc?.title || (isPost ? "Inlegg" : "Side");
  const title = titleBase.includes(SITE_NAME) ? titleBase : `${titleBase} - ${SITE_NAME}`;
  const description = documentExcerpt(doc, isPost ? "Artikkel fra Airon AS." : "");
  const keywords = documentKeywords(doc);
  const canonical = absoluteUrl(path || (isPost ? postPath(doc) : pagePath(doc)));
  const image = doc?.featured_image_url || null;
  const published = doc?.published_at || doc?.created_at || null;
  const modified = doc?.updated_at || published;

  return {
    title,
    description,
    keywords: keywords.join(", "),
    keywordList: keywords,
    robots: robotsContent(doc),
    canonical,
    image,
    published,
    modified,
    author: doc?.author_name || SITE_NAME,
    ogType: isPost ? "article" : "website",
    jsonLd: isPost
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: doc?.title || titleBase,
          description,
          datePublished: published,
          dateModified: modified,
          author: { "@type": "Organization", name: doc?.author_name || SITE_NAME },
          publisher: { "@type": "Organization", name: SITE_NAME },
          mainEntityOfPage: canonical,
          image: image || undefined,
          keywords: keywords.join(", ") || undefined,
          inLanguage: "nb-NO",
        }
      : {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: doc?.title || titleBase,
          description,
          url: canonical,
          inLanguage: "nb-NO",
          publisher: { "@type": "Organization", name: SITE_NAME },
        },
  };
}

export function sitemapXml(entries, { origin = SITE_ORIGIN } = {}) {
  const urls = (entries || [])
    .filter((entry) => entry?.loc || entry?.path)
    .map((entry) => {
      const loc = entry.loc || absoluteUrl(entry.path);
      const lastmod = entry.lastmod || "";
      const changefreq = entry.changefreq || "weekly";
      const priority = entry.priority || "0.7";
      return `  <url>\n    <loc>${escapeXml(loc)}</loc>\n    <lastmod>${escapeXml(lastmod)}</lastmod>\n    <changefreq>${escapeXml(changefreq)}</changefreq>\n    <priority>${escapeXml(priority)}</priority>\n  </url>`;
    })
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`.replace(
    origin,
    origin
  );
}

export function sitemapEntriesFromDocuments(pages = [], posts = []) {
  return [
    { path: "/", lastmod: new Date().toISOString(), changefreq: "daily", priority: "1.0" },
    { path: "/innlegg", lastmod: new Date().toISOString(), changefreq: "daily", priority: "0.8" },
    ...pages.map((page) => ({
      path: pagePath(page),
      lastmod: page.updated_at || page.published_at,
      changefreq: "weekly",
      priority: "0.7",
    })),
    ...posts.map((post) => ({
      path: postPath(post),
      lastmod: post.updated_at || post.published_at,
      changefreq: "weekly",
      priority: "0.8",
    })),
  ].filter((entry) => entry.path);
}

function escapeXml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function shouldShowPostsSection({ loading, items }) {
  if (loading) return false;
  return Array.isArray(items) && items.length > 0;
}

export function formatPostDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
